'use client';

import { useAuth } from '@clerk/nextjs';
import { Button } from '@components/ui/button';
import { useIsMobile } from '@hooks/use-mobile';
import { usePwa } from '@hooks/use-pwa';
import { cn } from '@utils/cn';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

export function pushPromptDismissedKey(userId: string): string {
  return `lqd:push-prompt-dismissed:${userId}`;
}

function readDismissed(userId: string | null | undefined): boolean {
  if (!userId) return false;
  try {
    return localStorage.getItem(pushPromptDismissedKey(userId)) === '1';
  } catch {
    // Private browsing / quota — treat as not dismissed.
    return false;
  }
}

function writeDismissed(userId: string | null | undefined): void {
  if (!userId) return;
  try {
    localStorage.setItem(pushPromptDismissedKey(userId), '1');
  } catch {
    // Private browsing / quota — the in-memory dismissal still applies.
  }
}

/**
 * In-flow, one-time nudge to turn on Web Push on a phone. Sits in the app
 * shell next to {@link PwaUpdateBanner} (not `position: fixed`) so it never
 * covers header circles or the messenger composer.
 *
 * `enablePush` runs synchronously inside the click handler: iOS only shows
 * the permission prompt while the user gesture is still active.
 */
export function PushPrompt({
  organizationId,
  className
}: {
  organizationId: string;
  className?: string;
}) {
  const { pushStatus, installable, promptInstall, enablePush, pushBusy } =
    usePwa();
  const { userId, isLoaded } = useAuth();
  const router = useRouter();
  const isMobile = useIsMobile();
  const t = useTranslations('pwa');
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    // Until Clerk resolves the user the dismissal cannot be read (or
    // persisted), so stay hidden rather than flashing a dismissed prompt.
    if (!isLoaded || !userId) return;
    setDismissed(readDismissed(userId));
  }, [userId, isLoaded]);

  const eligible = pushStatus === 'off' || pushStatus === 'needs-install';
  if (!isLoaded || !userId || !eligible || !isMobile || dismissed) return null;

  const needsInstall = pushStatus === 'needs-install';

  const dismiss = () => {
    setDismissed(true);
    writeDismissed(userId);
  };

  const onPrimary = () => {
    if (needsInstall) {
      if (installable) {
        void promptInstall();
      } else {
        router.push(`/${organizationId}/settings`);
      }
      return;
    }
    void enablePush();
  };

  return (
    <div
      role="region"
      aria-label={t('push-prompt-title')}
      className={cn(
        'shrink-0 px-3 pb-2 pt-1 desktop-chrome:px-7 desktop-chrome:pt-3',
        className
      )}
    >
      <div className="mx-auto flex max-w-lg flex-col gap-3 rounded-xl border border-border-strong bg-card px-4 py-3 shadow-md">
        <div className="flex flex-col gap-1">
          <p className="text-body-dense font-medium text-foreground">
            {t('push-prompt-title')}
          </p>
          <p className="text-caption text-secondary-foreground">
            {needsInstall
              ? t('push-prompt-install-body')
              : t('push-prompt-body')}
          </p>
        </div>
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={dismiss}>
            {t('push-prompt-dismiss')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={onPrimary}
            disabled={pushBusy}
          >
            {needsInstall ? t('push-prompt-install') : t('push-prompt-enable')}
          </Button>
        </div>
      </div>
    </div>
  );
}
